// src/components/ValidationDiffTable.tsx
import React from "react";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-enterprise";
import { FaCheckCircle, FaTimesCircle, FaPlusCircle } from "react-icons/fa";

import type { RoomValidationDiffItem } from "@backend/src/schemas";
import { CompCodeName } from "./CompCodeName";

interface ValidationDiffTableProps {
  diffItems: RoomValidationDiffItem[];
}

export const ValidationDiffTable: React.FC<ValidationDiffTableProps> = ({ diffItems }) => {
  const columnDefs: ColDef<RoomValidationDiffItem>[] = [
    {
      headerName: "",
      width: 50,
      cellRenderer: (params: any) => {
        const delta = params.data?.delta;
        if (delta === 0) return <FaCheckCircle color="#2f9e44" size={18} title="Match" />;
        if (delta < 0) return <FaTimesCircle color="#e03131" size={18} title="Missing" />;
        if (delta > 0) return <FaPlusCircle color="#1971c2" size={18} title="Extra" />;
        return null;
      },
      sortable: false,
      filter: false,
    },

    {
      headerName: "CompCode",
      field: "compCode",
      flex: 1,
      minWidth: 140,
      cellRenderer: (params: any) => {
        const compCode = params.value as string | undefined;
        const label = params.data?.label as string | undefined;

        return (
          <CompCodeName compCode={compCode} name={label} tooltipFallback="No type name" />
        );
      },
    },

    // Hidden column – useful for quick filter/search
    {
      headerName: "Type name",
      field: "label",
      hide: true,
    },

    {
      headerName: "Standard count",
      field: "standardCount",
      width: 150,
      type: "rightAligned",
      cellStyle: { fontWeight: 500 },
    },
    {
      headerName: "Instance count",
      field: "instanceCount",
      width: 150,
      type: "rightAligned",
    },
    {
      headerName: "Δ",
      field: "delta",
      width: 150,
      type: "rightAligned",
      cellStyle: (p) => ({
        color: p.value > 0 ? "#1971c2" : p.value < 0 ? "#e03131" : "#2f9e44",
        fontWeight: 600,
      }),
    },
  ];

  const defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  return (
    <div className="ag-theme-quartz" style={{ height: "100%", width: "100%" }}>
      <AgGridReact<RoomValidationDiffItem>
        rowData={diffItems}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        animateRows
      />
    </div>
  );
};
